import { memo, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import DeclineArrow from "@zenith/assets/icons/DeclineArrow";
import Received from "@zenith/assets/icons/Received";
import { useTransactionsContext } from "@zenith/context";
import {
  ApiResponseDTOPaginationRecordListTransactionBaseDTO,
  OperationDTO,
  TransactionBaseDTO,
  TransactionsApiService,
} from "@zenith/services/transactions";
import { formatCurrency } from "@zenith/utils";
import SpinnerComponent from "../../SpinnerComponent/spinner-component/spinner-component";
import { EmptyState } from "../../EmptyStateComponent/empty-state-component/empty-state-component";
import { NoTransaction } from "@zenith/assets/illustrations/NoTransaction";
import { set } from "lodash";
import ServerErrorHandler from "@zenith/features/AppStateHandlers/ServerErrorHandler/server-error-handler";
import { mapPaymentMethodWithType } from "@zenith/utils/ExtensionMethods";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";

/**
 * Props for Transaction List Component.
 * @interface
 * @name TransactionListComponentProps
 * @kind interface
 * @exports
 */
export interface TransactionListComponentProps {
  /**
   * (optional)
   * Transaction type (indicates if the money
   * is flowing `"IN"` or `"OUT"` of the merchant account).
   * @property
   * @name transactionType
   * @kind property
   * @memberof AmountBalanceParams
   * @type {TransactionBaseDTO.transactionType | OperationDTO.operationType | undefined}
   */
  transactionType?:
    | TransactionBaseDTO.transactionType
    | OperationDTO.operationType
    | string;
  /**
   * (optional)
   * Payment method.
   * @property
   * @name paymentMethod
   * @kind property
   * @memberof TransactionListComponentProps
   * @type {string | undefined}
   */
  paymentMethod?: string;
  /**
   * Transaction unique ID.
   * @property
   * @name transactionId
   * @kind property
   * @memberof TransactionListComponentProps
   * @type {string | undefined}
   */
  transactionId: string;
  /**
   * (optional)
   * Total amount.
   * @default 0.00
   * @property
   * @name totalAmount
   * @kind property
   * @memberof AmountBalanceParams
   * @type {number | undefined}
   */
  totalAmount?: number;
  /**
   * (optional)
   * Current account balance.
   * @default 0.00
   * @property
   * @name currentAccountBalance
   * @kind property
   * @memberof AmountBalanceParams
   * @type {number | undefined}
   */
  currentAccountBalance?: number;
}

/**
 * Interface for Amount Balance Params.
 * @interface
 * @name AmountBalanceParams
 * @kind interface
 */
interface AmountBalanceParams {
  /**
   * (optional)
   * Total amount.
   * @default 0.00
   * @property
   * @name totalAmount
   * @kind property
   * @memberof AmountBalanceParams
   * @type {number | undefined}
   */
  totalAmount?: number;
  /**
   * (optional)
   * Current account balance.
   * @default 0.00
   * @property
   * @name currentAccountBalance
   * @kind property
   * @memberof AmountBalanceParams
   * @type {number | undefined}
   */
  currentAccountBalance?: number;
  /**
   * (optional)
   * Transaction type (indicates if the money
   * is flowing `"IN"` or `"OUT"` of the merchant account).
   * @property
   * @name transactionType
   * @kind property
   * @memberof AmountBalanceParams
   * @type {TransactionBaseDTO.transactionType | OperationDTO.operationType | undefined}
   */
  transactionType?:
    | TransactionBaseDTO.transactionType
    | OperationDTO.operationType;
}

const PAGE_SIZE = 5;
const MAX_DESCRIPTION_LENGTH = 40;

/**
 * Helper function to truncate long text
 * @param text The text to truncate
 * @param maxLength Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Memoized Transaction List Component.
 * @constant
 * @name TransactionListComponent
 * @kind variable
 * @type {React.NamedExoticComponent<TransactionListComponentProps>}
 */
const TransactionListComponent: React.NamedExoticComponent<TransactionListComponentProps> =
  memo(
    /**
     * Transaction List Component.
     * @function
     * @name TransactionListComponent
     * @kind function
     * @memberof TransactionListComponent
     * @param {TransactionListComponentProps} props Props for Transaction List Component.
     * @returns {JSX.Element} Transaction List Component.
     */
    function TransactionListComponent(
      props: TransactionListComponentProps
    ): JSX.Element {
      const [transactionList, setTransactionList] =
        useState<TransactionBaseDTO[]>();
      const [serverError, setServerError] = useState(false);

      const { transactionsContext, setTransactionsContext } =
        useTransactionsContext();
      const [isLoading, setLoading] = useState(true);

      const { outletIdsForRequests, isLoadingOutlets } = useOutletView();

      // Recent transactions follow the outlet in view. When every outlet is in
      // view nothing is sent, and the API returns them across all of them.
      const requestOutletIds = outletIdsForRequests.length
        ? outletIdsForRequests
        : undefined;
      const requestOutletIdsKey = requestOutletIds?.join(",") ?? "all";

      // Tooltip props for description
      const descriptionTooltipProps = {
        componentsProps: {
          tooltip: {
            sx: {
              backgroundColor: "#373737",
              borderRadius: "8px",
              fontSize: "12px",
              color: "white",
              maxWidth: "300px",
              wordBreak: "break-word",
            },
          },
          arrow: {
            sx: {
              color: "#373737",
            },
          },
        },
      };

      useEffect(() => {
        // Every role has a list to read, and the server is what scopes it - a
        // cashier gets the transactions they collected. Fetching only for the
        // owner left every team member's dashboard permanently empty.
        //
        // Before the outlets are known no outlet filter can be sent, and the
        // server would answer for every outlet the caller may see.
        if (isLoadingOutlets) return;
        setLoading(true);
        TransactionsApiService.getTransactions(
          "",
          String(PAGE_SIZE),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          requestOutletIds?.join(",")
        )
          .then(
            (
              response: ApiResponseDTOPaginationRecordListTransactionBaseDTO
            ) => {
              if ("data" in response && response.data) {
                const content = response.data.content?.slice(0, PAGE_SIZE);
                const modifiedData = content?.map((transaction) => ({
                  ...transaction,
                  transactionId: truncateTransactionId(
                    transaction.transactionId as string
                  ),
                }));
                setTransactionList(modifiedData);
                setTransactionsContext({
                  ...transactionsContext,
                  hasTransactions:
                    (response?.data?.content?.length as number) > 0,
                  isOperation: false,
                });
              }
            }
          )
          .catch((error) => {
            console.error("Error fetching transactions:", error);
            setServerError(true);
          })
          .finally(() => setLoading(false));
      }, [requestOutletIdsKey, isLoadingOutlets]);

      const truncateTransactionId = (transactionId: string): string => {
        const segments = transactionId.split("-");
        const lastSegment = segments[segments.length - 1];
        return lastSegment;
      };

      // Helper function to format amount and balance
      const getAmountBalanceText = ({
        totalAmount,
        currentAccountBalance,
        transactionType,
      }: AmountBalanceParams) => {
        const isOut = transactionType === OperationDTO.operationType.OUT;
        const isIn = transactionType === OperationDTO.operationType.IN;

        // Format numbers to have two decimal places and comma separation
        const formattedTotalAmount = formatCurrency(totalAmount);

        return (
          <div className={`flex flex-col items-center px-2 py-2`}>
            <p
              className={`text-base ${
                isIn ? "text-green-400" : isOut ? "text-red-500" : ""
              }`}
            >
              {isOut && <span className="text-red-500">- </span>}
              {isIn && "+ "}
              <span>{formattedTotalAmount}</span>
            </p>
          </div>
        );
      };

      if (isLoading) {
        return (
          <div className="my-auto flex h-[calc(100vh-400px)] items-center justify-center">
            <SpinnerComponent size={40} type={"primary"} showIcon={false} />
          </div>
        );
      }

      return (
        <table className="w-full">
          <tbody>
            {serverError && (
              <tr className="my-auto flex w-full flex-col items-center justify-center">
                <ServerErrorHandler
                  errorTitle="We are unable to fetch transaction history"
                  errorMessage="Please try again. Apologies for the inconvenience."
                />
              </tr>
            )}
            {!serverError && !transactionList && (
              <tr className="my-auto flex w-full flex-col items-center justify-center">
                <EmptyState
                  title="No Transactions Found"
                  description="It seems there are no transactions to display at the moment"
                  illustration={
                    <div className="flex w-full justify-center">
                      <NoTransaction className="block h-36 w-100 text-primary-300" />
                    </div>
                  }
                />
              </tr>
            )}
            {transactionList &&
              transactionList?.map((transaction, index) => {
                const description =
                  transaction.description || transaction.transactionId || "";
                const isDescriptionLong =
                  description.length > MAX_DESCRIPTION_LENGTH;
                const truncatedDescription = truncateText(
                  description,
                  MAX_DESCRIPTION_LENGTH
                );

                return (
                  <tr
                    key={`${transaction.transactionId}-${index}`}
                    className={`flex h-16 items-center${
                      index !== transactionList.length - 1 ? " border-b" : ""
                    }`}
                  >
                    <td className="flex w-full items-center px-4">
                      <div className="flex w-10 items-center justify-center">
                        {transaction.transactionType ===
                        TransactionBaseDTO.transactionType.IN ? (
                          <Received />
                        ) : (
                          <DeclineArrow />
                        )}
                      </div>
                      <div className="flex min-h-[64px] flex-1 flex-col items-start justify-center px-3 md:px-6">
                        <p className="text-center text-xs font-medium leading-tight text-neutral-600 md:text-base">
                          {mapPaymentMethodWithType(
                            transaction.paymentMethod,
                            transaction.partialTransactionType
                          )}
                        </p>
                        {/* Description with truncation and tooltip */}
                        <div className="mt-1">
                          {isDescriptionLong ? (
                            <Tooltip
                              {...descriptionTooltipProps}
                              title={description}
                              placement="top-start"
                              arrow
                              disableInteractive={false}
                            >
                              <p className="w-30 cursor-pointer text-xs leading-tight text-neutral-400 hover:text-neutral-600 md:w-full md:text-sm">
                                {truncatedDescription}
                              </p>
                            </Tooltip>
                          ) : (
                            <p className="w-30 text-xs leading-tight text-neutral-400 md:w-full md:text-sm">
                              {description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex min-w-[120px] justify-end text-sm sm:text-base">
                        {getAmountBalanceText({
                          totalAmount: transaction.totalAmount,
                          currentAccountBalance:
                            transaction.currentAccountBalance,
                          transactionType: transaction.transactionType,
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      );
    }
  );

export default TransactionListComponent;
